import { FamilyTreeState } from '../types';

/**
 * Convert GraphQL family tree data to the format expected by computeFamilyTreeLayout
 */
export function convertGraphQLToLayoutData(state: FamilyTreeState) {
  const hiddenClientProfileIds: string[] =
    Object.entries(state.nodeConfigs || {}).flatMap(
      ([personId, nodeConfig]) => {
        if (!nodeConfig.showTile) {
          return [personId];
        }
        return [];
      }
    ) ?? [];

  // On the presentation slide, always hide hidden individuals regardless of the
  // saved "Show hidden individuals" setting. The trowser and in-app diagram
  // (presentationMode false) respect the checkbox.
  const showHiddenIndividuals = state.presentationMode
    ? false
    : (state.visualizationConfig?.showHiddenIndividuals ?? false);

  const persons =
    state.familyTreeData?.people?.flatMap((person) => {
      const { clientProfile, isSynthetic } = person;
      if (
        !showHiddenIndividuals &&
        hiddenClientProfileIds.includes(clientProfile.id)
      ) {
        return [];
      }

      return {
        id: clientProfile.id,
        name: clientProfile.displayName,
        isPrimary: clientProfile.isPrimary,
        personId: clientProfile.id,
        generation: person.generation,
        dateOfBirth: clientProfile.dateOfBirth,
        dateOfDeath: clientProfile.dateOfDeath,
        stateCode: clientProfile.address?.stateCode,
        isDeceased: clientProfile.isDeceased,
        notes: clientProfile.notes,
        isSynthetic: isSynthetic ?? false,
      };
    }) ?? [];

  const marriages =
    state.familyTreeData?.marriages?.flatMap((marriage) => {
      const spouse0Hidden =
        !showHiddenIndividuals &&
        hiddenClientProfileIds.includes(marriage.spouses[0] ?? '');
      const spouse1Hidden =
        !showHiddenIndividuals &&
        hiddenClientProfileIds.includes(marriage.spouses[1] ?? '');

      // If both spouses are hidden, filter out the entire marriage
      if (spouse0Hidden && spouse1Hidden) {
        return [];
      }

      // If only spouse 0 is hidden, keep only spouse 1
      if (spouse0Hidden) {
        return {
          ...marriage,
          spouses: marriage.spouses[1] ? [marriage.spouses[1]] : [],
        };
      }

      // If only spouse 1 is hidden, keep only spouse 0
      if (spouse1Hidden) {
        return {
          ...marriage,
          spouses: marriage.spouses[0] ? [marriage.spouses[0]] : [],
        };
      }

      // Both spouses are visible, return the marriage as-is
      return marriage;
    }) ?? [];

  return { persons, marriages };
}
